import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useTradingQuery } from '../../hooks/useTradingQuery';
import { createChart, ColorType, LineStyle, IChartApi, ISeriesApi, AreaSeries } from 'lightweight-charts';
import { tradingService } from '../../services/trading';
import { utcToPacific, getStartOfDayPacific } from '@trading-app/shared';
import { RegimeShadingPrimitive } from './RegimeShading';
import type { RegimeHistoryEntry, RegimePnLPeriod } from './RegimeShading';

/**
 * Get end of day in Pacific Time (11:59:59.999 PM PST/PDT)
 * Returns UTC timestamp for end of day Pacific time, handling DST correctly
 */
function getEndOfDayPacific(date?: Date): Date {
  const startOfDay = getStartOfDayPacific(date);
  // Add 24 hours minus 1 millisecond to get 11:59:59.999 PM
  return new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000 - 1);
}

interface ProfitOverTimeChartProps {
  accountFilter: string;
  tickerFilter: string;
  algorithmFilter: string;
  timeRange: 'today' | 'week' | 'month' | 'year' | 'all';
  regimeHistory?: RegimeHistoryEntry[];
  regimePnL?: RegimePnLPeriod[];
}

export function ProfitOverTimeChart({ accountFilter, tickerFilter, algorithmFilter, timeRange, regimeHistory = [], regimePnL = [] }: ProfitOverTimeChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const regimePrimitiveRef = useRef<RegimeShadingPrimitive | null>(null);

  // Tooltip state for hover display
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    date: string;
    value: string;
    regime: string | null;
    regimeColor: string | null;
    periodPnl: string | null;
    periodPnlPositive: boolean;
  }>({ visible: false, x: 0, y: 0, date: '', value: '', regime: null, regimeColor: null, periodPnl: null, periodPnlPositive: false });

  // Format currency with commas
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Fetch ALL trade aggregates (closed trades with P&L)
  // Always fetch ALL trades so we can calculate the cumulative P&L from the beginning
  // This ensures the chart shows the correct starting value for filtered time ranges
  const { data: aggregates = [], isLoading } = useTradingQuery({
    queryKey: ['tradeAggregates', accountFilter, tickerFilter, algorithmFilter, 'all'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeAggregates({
          accountId: accountFilter || undefined,
          ticker: tickerFilter || undefined,
          algorithm: algorithmFilter || undefined,
          timeRange: 'all', // Always fetch all trades for accurate cumulative P&L
          limit: 10000,
        });
      } catch (err) {
        console.error('Trade aggregates fetch error:', err);
        return [];
      }
    },
  });

  // Calculate cumulative profit over time from aggregates
  const chartData = useMemo(() => {
    console.log('Total aggregates loaded:', aggregates.length);

    // Sort by close_time
    const sortedAggregates = [...aggregates].sort((a, b) => {
      const dateA = new Date(a.closeTime || 0).getTime();
      const dateB = new Date(b.closeTime || 0).getTime();
      return dateA - dateB;
    });

    // Helper to get today's date key in Pacific timezone
    const getTodayDateKey = (): string => {
      return new Date().toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
    };

    // Helper to find the last trading day info from aggregates
    // Returns both the date key AND the actual closeTime for proper timezone handling
    const findLastTradingDayInfo = (): { dateKey: string; closeTime: Date } | null => {
      const dateMap = new Map<string, Date>();
      sortedAggregates.forEach(agg => {
        if (agg.closeTime) {
          const closeDate = new Date(agg.closeTime);
          const dateKey = closeDate.toLocaleDateString('en-CA', {
            timeZone: 'America/Los_Angeles'
          });
          // Store the actual Date object (not reconstructed from string)
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, closeDate);
          }
        }
      });
      const sortedDates = Array.from(dateMap.keys()).sort().reverse();
      if (sortedDates.length > 0) {
        const lastDateKey = sortedDates[0];
        return { dateKey: lastDateKey, closeTime: dateMap.get(lastDateKey)! };
      }
      return null;
    };

    // Check if we need to fall back to the last trading day when 'today' is selected
    let fallbackDateKey: string | null = null;
    let fallbackCloseTime: Date | null = null;

    if (timeRange === 'today') {
      const todayKey = getTodayDateKey();
      const hasTodayTrades = sortedAggregates.some(agg => {
        if (!agg.closeTime) return false;
        const aggDateKey = new Date(agg.closeTime).toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
        return aggDateKey === todayKey;
      });

      if (!hasTodayTrades) {
        // No trades today - fall back to last trading day
        const lastTradingDayInfo = findLastTradingDayInfo();
        if (lastTradingDayInfo) {
          fallbackDateKey = lastTradingDayInfo.dateKey;
          fallbackCloseTime = lastTradingDayInfo.closeTime;
          console.log('No trades today, falling back to last trading day:', fallbackDateKey);
        }
      }
    }

    // Calculate the start date based on time range
    const getStartDateForTimeRange = (): Date => {
      const now = new Date();
      
      // If we're falling back to the last trading day, use the actual closeTime
      // to create a start-of-day in the correct timezone context
      if (fallbackCloseTime) {
        // Use DST-aware helper to get midnight Pacific time
        return getStartOfDayPacific(fallbackCloseTime);
      }
      
      switch (timeRange) {
        case 'today':
          const todayStart = new Date(now);
          todayStart.setHours(0, 0, 0, 0);
          return todayStart;
        case 'week':
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - 7);
          weekStart.setHours(0, 0, 0, 0);
          return weekStart;
        case 'month':
          const monthStart = new Date(now);
          monthStart.setDate(monthStart.getDate() - 30);
          monthStart.setHours(0, 0, 0, 0);
          return monthStart;
        case 'year':
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return yearStart;
        case 'all':
        default:
          // For 'all', we'll determine the date from the data
          return new Date(0);
      }
    };

    const periodStartDate = getStartDateForTimeRange();

    // Calculate cumulative P&L, tracking the P&L at the start of the period
    let cumulativePnL = 0;
    let priorPnL = 0; // P&L accumulated before the selected time period
    const dataPoints: Array<{ date: Date; profit: number; aggregate: any }> = [];

    sortedAggregates.forEach(agg => {
      if (agg.closeTime) {
        cumulativePnL += agg.netPnl;
        // Convert UTC close_time to Pacific Time for display
        const pacificDate = utcToPacific(agg.closeTime);

        // Check if this trade is before the period start
        if (timeRange !== 'all' && pacificDate.getTime() < periodStartDate.getTime()) {
          priorPnL = cumulativePnL;
        } else if (fallbackDateKey) {
          // When showing a fallback day, only include trades from that specific day
          const aggDateKey = new Date(agg.closeTime).toLocaleDateString('en-CA', {
            timeZone: 'America/Los_Angeles'
          });
          if (aggDateKey === fallbackDateKey) {
            dataPoints.push({
              date: pacificDate,
              profit: cumulativePnL,
              aggregate: agg,
            });
          }
        } else {
          dataPoints.push({
            date: pacificDate,
            profit: cumulativePnL,
            aggregate: agg,
          });
        }
      }
    });

    console.log('Total data points in period:', dataPoints.length);
    console.log('Prior P&L (before period):', priorPnL);

    // Group by day (take last point of each day for end-of-day profit)
    const dailyDataMap = new Map<string, { date: Date; profit: number; aggregate: any }>();

    dataPoints.forEach(point => {
      // Create date key in YYYY-MM-DD format using Pacific timezone
      const dateKey = point.date.toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
      // Keep the latest point for each day
      dailyDataMap.set(dateKey, point);
    });

    // Convert to array and sort
    const dailyPoints = Array.from(dailyDataMap.values()).sort((a, b) =>
      a.date.getTime() - b.date.getTime()
    );

    console.log('Daily data points:', dailyPoints.length);

    // Add starting point at the beginning of the time period
    if (timeRange !== 'all') {
      // For filtered time ranges, add a starting point with the prior cumulative P&L
      const firstDataDate = dailyPoints.length > 0 
        ? new Date(dailyPoints[0].date) 
        : new Date();
      firstDataDate.setHours(0, 0, 0, 0);
      
      // Only add starting point if period start is before or equal to first data point
      if (periodStartDate.getTime() <= firstDataDate.getTime()) {
        dailyPoints.unshift({
          date: periodStartDate,
          profit: priorPnL, // Use the cumulative P&L from before the period
          aggregate: null,
        });
      }
    } else if (dailyPoints.length > 0) {
      // For 'all' time range, add a zero point at the start of the first day
      const firstDate = new Date(dailyPoints[0].date);
      firstDate.setHours(0, 0, 0, 0);
      dailyPoints.unshift({
        date: firstDate,
        profit: 0,
        aggregate: null,
      });
    }

    // Add end point to ensure chart extends to the end of the period
    if (dailyPoints.length > 0) {
      const lastPoint = dailyPoints[dailyPoints.length - 1];
      // Get Pacific date key for last data point
      const lastDataDateKey = lastPoint.date.toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
      
      // Determine the end date (today or the fallback day's end) in Pacific time
      let endDateKey: string;
      if (fallbackCloseTime) {
        // Use the fallback day's date in Pacific timezone
        endDateKey = fallbackCloseTime.toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
      } else {
        // Use today's date in Pacific timezone
        endDateKey = new Date().toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
      }

      // Add daily interpolation points between last trade and today so regime
      // shading can color each day individually (not just a single jump)
      if (lastDataDateKey < endDateKey) {
        const lastDate = new Date(lastDataDateKey + 'T12:00:00');
        const endDateParsed = new Date(endDateKey + 'T12:00:00');
        const oneDay = 86400000;

        for (
          let d = new Date(lastDate.getTime() + oneDay);
          d <= endDateParsed;
          d = new Date(d.getTime() + oneDay)
        ) {
          const dayKey = d.toISOString().slice(0, 10);
          const dayOfWeek = d.getUTCDay();
          // Skip weekends — regime only exists on trading days
          if (dayOfWeek === 0 || dayOfWeek === 6) continue;
          const [y, m, dy] = dayKey.split('-').map(Number);
          const ref = new Date(Date.UTC(y, m - 1, dy, 12, 0, 0));
          const eod = getEndOfDayPacific(ref);
          dailyPoints.push({
            date: eod,
            profit: lastPoint.profit,
            aggregate: null,
          });
        }
      }
    }

    if (dailyPoints.length > 0) {
      console.log('First point:', dailyPoints[0]);
      console.log('Last point:', dailyPoints[dailyPoints.length - 1]);
    }

    return dailyPoints;
  }, [aggregates, timeRange]);

  // Create and manage TradingView chart
  useEffect(() => {
    if (!chartContainerRef.current || !chartData || chartData.length === 0) {
      console.log('Chart init skipped:', {
        hasContainer: !!chartContainerRef.current,
        dataLength: chartData?.length || 0
      });
      return;
    }

    try {
      // Clean up previous chart if it exists
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }

      // Ensure container has width
      const containerWidth = chartContainerRef.current.clientWidth;
      console.log('Chart container width:', containerWidth);
      if (containerWidth === 0) {
        console.warn('Chart container has no width, skipping initialization');
        return;
      }

      // Determine if profit is positive or negative
      const currentProfit = chartData.length > 0 ? chartData[chartData.length - 1].profit : 0;
      const isPositive = currentProfit >= 0;
      const lineColor = isPositive ? '#00C805' : '#FF5000';
      
      console.log('Chart data points:', chartData.length);
      console.log('Current profit:', currentProfit);
      console.log('Line color:', lineColor);

      // Create chart with options matching working PortfolioChart
      const chart = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: '#6B7280',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        width: containerWidth,
        height: 300,
        autoSize: true, // Auto-resize to fill container
        grid: {
          vertLines: {
            visible: false,
          },
          horzLines: {
            visible: false,
          },
        },
        crosshair: {
          mode: 1, // Normal mode
          vertLine: {
            visible: true,
            labelVisible: false,
          },
          horzLine: {
            visible: true,
            labelVisible: true,
          },
        },
        kineticScroll: {
          mouse: false,
          touch: false,
        },
        rightPriceScale: {
          visible: false,
          borderVisible: false,
          scaleMargins: {
            top: 0.05,
            bottom: 0.05,
          },
        },
        leftPriceScale: {
          visible: false,
        },
        timeScale: {
          visible: false,
          borderVisible: false,
          rightOffset: 0,
        },
        handleScroll: false,
        handleScale: false,
      });

      // Add area series — fill is transparent when regime shading is active
      const hasRegimeData = regimeHistory && regimeHistory.length > 0;
      const series = chart.addSeries(AreaSeries, {
        lineColor: lineColor,
        topColor: hasRegimeData ? 'transparent' : lineColor + '40',
        bottomColor: hasRegimeData ? 'transparent' : lineColor + '00',
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        crosshairMarkerBorderColor: '#FFFFFF',
        crosshairMarkerBackgroundColor: lineColor,
      });

      // Convert data to TradingView format
      const tvData = chartData
        .map(point => {
          const timestamp = new Date(point.date).getTime();
          if (isNaN(timestamp)) {
            console.warn('Invalid date:', point.date);
            return null;
          }
          return {
            time: Math.floor(timestamp / 1000) as any,
            value: point.profit,
          };
        })
        .filter(point => point !== null) as any[];

      console.log('TradingView data points:', tvData.length);
      console.log('Sample data:', tvData.slice(0, 3));

      if (tvData.length > 0) {
        series.setData(tvData);
        
        // Set visible range to exactly span all data points (fills full width)
        chart.timeScale().setVisibleLogicalRange({
          from: 0,
          to: tvData.length - 1,
        });
        
        console.log('Chart data set successfully');
      } else {
        console.warn('No valid data points to display');
      }

      chartRef.current = chart;
      seriesRef.current = series;

      // Attach regime shading primitive — draws colored area fills below the line
      const regimePrimitive = new RegimeShadingPrimitive();
      series.attachPrimitive(regimePrimitive);
      regimePrimitiveRef.current = regimePrimitive;
      if (regimeHistory.length > 0) {
        regimePrimitive.updateRegimes(regimeHistory);
      }
      if (tvData.length > 0) {
        regimePrimitive.updateChartData(tvData);
      }
      if (regimePnL.length > 0) {
        regimePrimitive.updatePnLPeriods(regimePnL);
      }

      // Subscribe to crosshair move for tooltip
      chart.subscribeCrosshairMove((param) => {
        if (!param.point || param.point.x < 0 || param.point.y < 0) {
          setTooltip(prev => ({ ...prev, visible: false }));
          return;
        }

        const data = param.seriesData.get(series);
        if (data && 'value' in data) {
          // Format date from timestamp - use Pacific timezone for consistency
          const timestamp = param.time as number;
          const date = new Date(timestamp * 1000);
          const formattedDate = date.toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

          // Look up regime and P&L for this date
          const dateKey = date.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
          let regimeLabel: string | null = null;
          let regimeColor: string | null = null;
          let periodPnl: string | null = null;
          let periodPnlPositive = false;

          if (regimeHistory.length > 0) {
            // Find regime for this date (exact or nearest previous)
            const sorted = [...regimeHistory].sort((a, b) => a.tradingDate.localeCompare(b.tradingDate));
            let match = sorted.find(r => r.tradingDate === dateKey);
            if (!match) {
              for (let i = sorted.length - 1; i >= 0; i--) {
                if (sorted[i].tradingDate <= dateKey) { match = sorted[i]; break; }
              }
            }
            if (match) {
              const labels: Record<string, string> = { favorable: 'Favorable', cautious: 'Cautious', adverse: 'Adverse' };
              const colors: Record<string, string> = { favorable: '#22C55E', cautious: '#FBBF24', adverse: '#DC2626' };
              regimeLabel = labels[match.regime] || match.regime;
              regimeColor = colors[match.regime] || '#888';
            }
          }

          if (regimePnL.length > 0) {
            const period = regimePnL.find(p => dateKey >= p.startDate && dateKey <= p.endDate);
            if (period) {
              const sign = period.netPnl >= 0 ? '+' : '-';
              periodPnl = `${sign}$${Math.abs(Math.round(period.netPnl)).toLocaleString()}`;
              periodPnlPositive = period.netPnl >= 0;
            }
          }

          setTooltip({
            visible: true,
            x: param.point.x,
            y: param.point.y,
            date: formattedDate,
            value: formatCurrency(data.value),
            regime: regimeLabel,
            regimeColor,
            periodPnl,
            periodPnlPositive,
          });
        } else {
          setTooltip(prev => ({ ...prev, visible: false }));
        }
      });

      // Apply custom price format
      chart.applyOptions({
        localization: {
          priceFormatter: (price: number) => {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(price);
          },
        },
      });

      // Handle resize - with autoSize: true, the chart handles this automatically
      const handleResize = () => {
        if (chartRef.current && tvData.length > 0) {
          // Re-apply visible range to maintain full width after resize
          chartRef.current.timeScale().setVisibleLogicalRange({
            from: 0,
            to: tvData.length - 1,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
          seriesRef.current = null;
          regimePrimitiveRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }, [chartData]);

  // Update regime shading when regime history changes
  useEffect(() => {
    if (regimePrimitiveRef.current) {
      if (regimeHistory.length > 0) {
        regimePrimitiveRef.current.updateRegimes(regimeHistory);
      }
    }
  }, [regimeHistory]);

  // Update P&L labels when regime P&L data arrives
  useEffect(() => {
    if (regimePrimitiveRef.current && regimePnL.length > 0) {
      regimePrimitiveRef.current.updatePnLPeriods(regimePnL);
    }
  }, [regimePnL]);

  // Generate dynamic title based on time range filter
  const getTimeRangeTitle = () => {
    switch (timeRange) {
      case 'today':
        return 'Profit Today';
      case 'week':
        return 'Profit This Week';
      case 'month':
        return 'Profit This Month';
      case 'year':
        return 'Profit This Year';
      case 'all':
        return 'Profit All Time';
      default:
        return 'Profit Over Time';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading profit chart...</div>
      </div>
    );
  }

  const currentProfit = chartData.length > 0 ? chartData[chartData.length - 1].profit : 0;

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        No trades found for selected filters
      </div>
    );
  }

  return (
    <div className="w-full relative tradingview-widget-container" style={{ minHeight: '300px' }}>
      <div 
        ref={chartContainerRef}
        className="w-full tradingview-chart"
        style={{ height: '300px', width: '100%' }}
      />
      {/* Tooltip overlay */}
      {tooltip.visible && (
        <div
          className="absolute pointer-events-none z-10 px-3 py-2 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y - 40,
            transform: tooltip.x > (chartContainerRef.current?.clientWidth || 0) - 120 ? 'translateX(-100%)' : 'none',
          }}
        >
          <div className="text-xs text-gray-500 dark:text-gray-400">{tooltip.date}</div>
          <div className={`text-sm font-semibold ${
            parseFloat(tooltip.value.replace(/[^0-9.-]/g, '')) >= 0
              ? 'text-rh-green'
              : 'text-rh-red'
          }`}>
            {tooltip.value}
          </div>
          {tooltip.regime && (
            <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-gray-200/30 dark:border-gray-600/30">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tooltip.regimeColor || '#888' }} />
              <span className="text-[10px] font-medium" style={{ color: tooltip.regimeColor || '#888' }}>
                {tooltip.regime}
              </span>
              {tooltip.periodPnl && (
                <span className={`text-[10px] font-semibold ml-auto ${tooltip.periodPnlPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {tooltip.periodPnl}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      <style>{`
        .tradingview-widget-container div[style*="absolute"][style*="bottom"] {
          display: none !important;
        }
        .tradingview-chart a {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        .tradingview-chart > div > div:nth-last-child(1) {
          display: none !important;
        }
        .tradingview-chart > div > div:nth-last-child(2) {
          display: none !important;
        }
        .tradingview-chart [data-role="watermark"] {
          display: none !important;
        }
      `}</style>
    </div>
  );
}
